import React from 'react';
import { Link } from 'react-router-dom';
import {
  UtensilsCrossed, Home as HomeIcon, Grid3x3,
  PlusSquare, Paintbrush, Building, Building2,
  Briefcase, CheckCircle, Clock, Award, Shield, ArrowRight,
  Phone, Flame, Wrench, Ruler,
} from 'lucide-react';

const HERO_IMG = "https://images.unsplash.com/photo-1600585154340-be6161a56a0c?w=1400&q=85";
const ABOUT_IMG = "https://images.unsplash.com/photo-1504307651254-35680f356dfd?w=900&q=85";
const PROCESS_BG = "https://images.unsplash.com/photo-1765277789186-04b71a9afd40?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85";

const services = [
  { icon: Building2, title: "Custom Home Build", desc: "Fully custom homes designed, engineered and built in-house from the ground up." },
  { icon: Briefcase, title: "Commercial Finishing", desc: "High-performance commercial interiors built to code, tailored to your business." },
  { icon: Building, title: "Condominium Finishing", desc: "Condo-compliant finishing meeting all building codes and board bylaws." },
  { icon: Flame, title: "Plaster Moulding & Fireplace Mantle", desc: "Custom decorative plasterwork, crown mouldings and bespoke fireplace mantles." },
  { icon: HomeIcon, title: "Residential Renovation", desc: "Complete residential transformations from single rooms to whole-home makeovers." },
  { icon: PlusSquare, title: "Home Additions", desc: "Second-storey additions, bump-outs and garage conversions built to last." },
  { icon: UtensilsCrossed, title: "Kitchen Cabinets & Refinishing", desc: "Custom cabinetry installation, refacing and finishing for any kitchen style." },
  { icon: Paintbrush, title: "Painting & Decoration", desc: "Interior and exterior painting with professional-grade, durable finishes." },
  { icon: Grid3x3, title: "Flooring & Tiling", desc: "Hardwood, vinyl, laminate, ceramic and natural stone installation." },
  { icon: Wrench, title: "Restoration", desc: "Expert restoration of heritage and damaged properties, inside and out." },
  { icon: Ruler, title: "Millwork", desc: "Custom built-ins, trim, shelving and architectural woodwork crafted in-house." },
];

const whyUs = [
  { icon: Shield, title: "Licensed & Insured", desc: "Every contractor is fully licensed, WSIB-covered and insured." },
  { icon: Award, title: "Quality Craftsmanship", desc: "Premium materials and experienced tradespeople on every project." },
  { icon: CheckCircle, title: "Full-Service Warranty", desc: "Every project comes with a comprehensive post-renovation warranty." },
  { icon: Clock, title: "Free Consultation", desc: "Start with a one-on-one consultation and receive a detailed quote." },
];

const steps = [
  { num: "01", title: "Consultation & Quote", desc: "We meet to understand your goals, budget and timeline. You receive a detailed fixed-scope quote — no surprises." },
  { num: "02", title: "Design & Planning", desc: "Our team develops custom floor plans and selects finishes that suit your lifestyle and budget." },
  { num: "03", title: "Permitting & Build", desc: "We secure all permits and begin construction with licensed trades and engineered materials." },
  { num: "04", title: "Walkthrough & Warranty", desc: "A detailed final walkthrough and full-service warranty gives you lasting peace of mind." },
];

const portfolioPreview = [
  { title: "Contemporary Kitchen", location: "Toronto, ON", img: "https://images.unsplash.com/photo-1639751787355-bbc3ed1fd639?w=600&q=80" },
  { title: "Luxury Master Bathroom", location: "Mississauga, ON", img: "https://images.unsplash.com/photo-1774578342098-66adff9c1fe1?w=600&q=80" },
  { title: "Custom Home Build", location: "Barrie, ON", img: "https://images.unsplash.com/photo-1600585154340-be6161a56a0c?w=600&q=80" },
  { title: "Modern Kitchen Island", location: "Hamilton, ON", img: "https://images.unsplash.com/photo-1638886043487-72d203fa66b6?w=600&q=80" },
  { title: "Spa-Style Bathroom", location: "Burlington, ON", img: "https://images.pexels.com/photos/32998547/pexels-photo-32998547.jpeg?auto=compress&cs=tinysrgb&w=600" },
  { title: "Open-Concept Living", location: "Oakville, ON", img: "https://images.unsplash.com/photo-1560448204-e02f11c3d0e2?w=600&q=80" },
];

export default function Home() {
  return (
    <main data-testid="home-page">

      {/* HERO */}
      <section className="min-h-screen flex items-center pt-20 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12 w-full">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center py-16 lg:py-0 min-h-[calc(100vh-80px)]">
            {/* Text */}
            <div className="lg:col-span-5 flex flex-col justify-center">
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#00A2E8] mb-4 fade-in-up">
                PREMIER GENERAL CONTRACTING IN ONTARIO
              </p>
              <h1 className="text-5xl md:text-6xl font-black uppercase tracking-tighter leading-none text-slate-900 mb-6 fade-in-up delay-100">
                WE BUILD<br />YOUR DREAM<br />SPACE.
              </h1>
              <p className="text-base md:text-lg text-slate-600 leading-relaxed mb-8 max-w-md fade-in-up delay-200">
                Expert craftsmanship and structural integrity for residential and commercial projects across Ontario.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 fade-in-up delay-300">
                <Link
                  to="/contact"
                  data-testid="hero-get-quote-btn"
                  className="bg-[#00A2E8] hover:bg-[#0088C4] text-white px-8 py-4 font-bold text-sm tracking-widest transition-colors duration-200 text-center"
                >
                  GET A FREE QUOTE
                </Link>
                <Link
                  to="/portfolio"
                  data-testid="hero-view-portfolio-btn"
                  className="border-2 border-slate-900 text-slate-900 hover:bg-slate-900 hover:text-white px-8 py-4 font-bold text-sm tracking-widest transition-all duration-200 text-center"
                >
                  VIEW PORTFOLIO
                </Link>
              </div>
              <div className="mt-10 pt-8 border-t border-slate-200 flex gap-8 fade-in-up delay-400">
                <div>
                  <div className="text-2xl font-black text-slate-900">25+</div>
                  <div className="text-xs text-slate-500 font-medium tracking-wide">Years Experience</div>
                </div>
                <div className="border-l border-slate-200 pl-8">
                  <div className="text-2xl font-black text-slate-900">Free</div>
                  <div className="text-xs text-slate-500 font-medium tracking-wide">Consultation</div>
                </div>
                <div className="border-l border-slate-200 pl-8">
                  <div className="text-2xl font-black text-slate-900">11+</div>
                  <div className="text-xs text-slate-500 font-medium tracking-wide">Services Offered</div>
                </div>
              </div>
            </div>

            {/* Image */}
            <div className="lg:col-span-7 relative h-[450px] lg:h-full overflow-hidden">
              <img
                src={HERO_IMG}
                alt="Modern project by Ariana General Contracting"
                className="w-full h-full object-cover"
                data-testid="hero-image"
              />
              <div className="absolute bottom-6 left-6 bg-white p-4 shadow-lg max-w-xs hidden md:block">
                <p className="text-xs font-bold uppercase tracking-widest text-[#00A2E8] mb-1">Serving</p>
            <p className="text-sm font-bold text-slate-900">Ontario</p>
                <p className="text-xs text-slate-500">Toronto · Hamilton · Barrie · Kitchener & more</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* STATS BAR */}
      <section className="bg-[#00A2E8] py-6" data-testid="stats-bar">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center text-white">
            {[
              { label: "Licensed & Insured", value: "Fully" },
              { label: "Service Area", value: "Ontario" },
              { label: "Consultation", value: "Free" },
              { label: "Experience", value: "25+ Years" },
            ].map(s => (
              <div key={s.label} className="py-2">
                <div className="text-xl md:text-2xl font-black tracking-tight">{s.value}</div>
                <div className="text-xs font-semibold tracking-wider opacity-80 mt-1 uppercase">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SERVICES */}
      <section className="py-20 md:py-32 bg-white" data-testid="services-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="mb-12">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#00A2E8] mb-3">WHAT WE DO</p>
            <h2 className="text-3xl md:text-4xl font-extrabold tracking-tight text-slate-900 mb-4">Our Services</h2>
            <p className="text-base text-slate-600 max-w-2xl">
              From concept to completion, Ariana General Contracting provides complete build-outs for residential and commercial spaces. We handle everything from demolition to final finishes. Through our professional team of designers and builders we are committed to deliver quality workmanship, transparent communication, and reliable execution on every project.
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 border-l border-t border-slate-200">
            {services.map((s, i) => {
              const Icon = s.icon;
              return (
                <div
                  key={i}
                  data-testid={`service-card-${i}`}
                  className="p-6 border-r border-b border-slate-200 bg-white hover:bg-[#00A2E8]/5 hover:-translate-y-1 transition-all duration-300 group"
                >
                  <Icon className="w-8 h-8 text-[#00A2E8] mb-4 group-hover:scale-110 transition-transform" strokeWidth={1.5} />
                  <h3 className="text-base font-bold text-slate-900 mb-2">{s.title}</h3>
                  <p className="text-sm text-slate-500 leading-relaxed">{s.desc}</p>
                </div>
              );
            })}
            {/* Filler cell for grid alignment */}
            <div className="p-6 border-r border-b border-slate-200 bg-[#00A2E8] flex flex-col justify-between">
              <p className="text-white font-bold text-sm uppercase tracking-widest">Ready to get started?</p>
              <Link to="/contact" className="mt-4 text-white font-black text-sm flex items-center gap-2 hover:gap-3 transition-all duration-200">
                GET A QUOTE <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
          <div className="mt-8 text-center">
            <Link to="/services" data-testid="view-all-services-btn" className="inline-block border-2 border-[#00A2E8] text-[#00A2E8] hover:bg-[#00A2E8] hover:text-white px-8 py-3 font-bold text-sm tracking-widest transition-all duration-200">
              VIEW ALL SERVICES
            </Link>
          </div>
        </div>
      </section>

      {/* ABOUT SNIPPET */}
      <section className="py-20 md:py-32 bg-slate-50" data-testid="about-snippet">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <div className="relative">
              <img
                src={ABOUT_IMG}
                alt="Ariana General Contracting team at work"
                className="w-full h-[450px] object-cover"
              />
              <div className="absolute -bottom-6 -right-6 bg-[#00A2E8] text-white p-6 w-40 hidden md:block">
                <p className="text-3xl font-black leading-none">11+</p>
                <p className="text-xs font-semibold tracking-wide mt-1 uppercase opacity-90">Services<br />Offered</p>
              </div>
            </div>
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#00A2E8] mb-3">ABOUT ARIANA</p>
              <h2 className="text-3xl md:text-4xl font-extrabold tracking-tight text-slate-900 mb-6">
                Your trusted Building partner in Ontario
              </h2>
              <p className="text-base text-slate-600 leading-relaxed mb-4">
                Ariana General Contracting was founded with a single mission: to deliver exceptional building and finishing services with integrity. Over 25 years of experience, we have built our reputation on quality craftsmanship, transparent communication, and results that exceed expectations.
              </p>
              <p className="text-base text-slate-600 leading-relaxed mb-8">
                We serve homeowners and businesses across Ontario — from Toronto to Barrie, Hamilton to Kitchener. Every project is fully managed in-house, from initial consultation through permits, construction, and final walkthrough.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link
                  to="/about"
                  data-testid="learn-more-btn"
                  className="bg-[#00A2E8] hover:bg-[#0088C4] text-white px-8 py-4 font-bold text-sm tracking-widest transition-colors duration-200 text-center"
                >
                  LEARN MORE
                </Link>
                <a
                  href="tel:+14165703834"
                  data-testid="call-us-btn"
                  className="border-2 border-slate-300 text-slate-700 hover:border-[#00A2E8] hover:text-[#00A2E8] px-8 py-4 font-bold text-sm tracking-widest transition-all duration-200 text-center flex items-center justify-center gap-2"
                >
                  <Phone className="w-4 h-4" /> CALL US
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* WHY CHOOSE US */}
      <section className="py-20 md:py-32 bg-white" data-testid="why-choose-us">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-14">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#00A2E8] mb-3">WHY CHOOSE US</p>
            <h2 className="text-3xl md:text-4xl font-extrabold tracking-tight text-slate-900">
              The Ariana Difference
            </h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {whyUs.map((item, i) => {
              const Icon = item.icon;
              return (
                <div key={i} data-testid={`why-us-card-${i}`} className="text-center p-6 border border-slate-200 hover:border-[#00A2E8] hover:shadow-[0_10px_40px_-10px_rgba(0,162,232,0.15)] transition-all duration-300 group">
                  <div className="w-14 h-14 bg-[#00A2E8]/10 flex items-center justify-center mx-auto mb-4 group-hover:bg-[#00A2E8] transition-colors duration-300">
                    <Icon className="w-7 h-7 text-[#00A2E8] group-hover:text-white transition-colors duration-300" strokeWidth={1.5} />
                  </div>
                  <h3 className="text-base font-bold text-slate-900 mb-2">{item.title}</h3>
                  <p className="text-sm text-slate-500 leading-relaxed">{item.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* PROCESS */}
      <section
        className="py-20 md:py-32 relative"
        style={{ backgroundImage: `url(${PROCESS_BG})`, backgroundSize: 'cover', backgroundPosition: 'center' }}
        data-testid="process-section"
      >
        <div className="absolute inset-0 bg-slate-950/88" />
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-14">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#00A2E8] mb-3">HOW IT WORKS</p>
            <h2 className="text-3xl md:text-4xl font-extrabold tracking-tight text-slate-900">
              Our Process
            </h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {steps.map((step, i) => (
              <div key={i} data-testid={`process-step-${i}`} className="relative">
                <div className="text-6xl font-black text-slate-900 leading-none mb-2">{step.num}</div>
                <h3 className="text-lg font-bold text-slate-900 mb-3">{step.title}</h3>
                <p className="text-sm text-slate-800 leading-relaxed">{step.desc}</p>
                {i < steps.length - 1 && (
                  <div className="hidden lg:block absolute top-8 left-full w-8 h-px bg-[#00A2E8]/40 -translate-x-4" />
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PORTFOLIO PREVIEW */}
      <section className="py-20 md:py-32 bg-slate-50" data-testid="portfolio-preview">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-12">
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#00A2E8] mb-3">OUR WORK</p>
              <h2 className="text-3xl md:text-4xl font-extrabold tracking-tight text-slate-900">Recent Projects</h2>
            </div>
            <Link to="/portfolio" data-testid="view-all-projects-btn" className="shrink-0 flex items-center gap-2 font-bold text-sm text-[#00A2E8] hover:gap-3 transition-all duration-200 tracking-wide">
              VIEW ALL PROJECTS <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {portfolioPreview.map((item, i) => (
              <div key={i} data-testid={`portfolio-preview-${i}`} className="group overflow-hidden relative">
                <div className="overflow-hidden h-64">
                  <img
                    src={item.img}
                    alt={item.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                  />
                </div>
                <div className="absolute inset-0 bg-slate-900/0 group-hover:bg-slate-900/60 transition-all duration-300 flex items-end">
                  <div className="p-5 translate-y-4 group-hover:translate-y-0 opacity-0 group-hover:opacity-100 transition-all duration-300">
                    <h3 className="text-white font-bold text-sm">{item.title}</h3>
                    <p className="text-slate-300 text-xs mt-1">{item.location}</p>
                  </div>
                </div>
                <div className="mt-3">
                  <h3 className="font-bold text-slate-900 text-sm">{item.title}</h3>
                  <p className="text-xs text-slate-500 mt-1">{item.location}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CONTACT CTA */}
      <section className="py-20 md:py-28 bg-[#00A2E8]" data-testid="home-contact-cta">
        <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
          <h2 className="text-3xl md:text-4xl font-black uppercase tracking-tight text-white mb-4">
            Ready to Transform Your Space?
          </h2>
          <p className="text-white/80 text-base md:text-lg max-w-2xl mx-auto mb-8">
            Contact us today for a free, no-obligation consultation. We serve all of Ontario.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/contact"
              data-testid="cta-contact-btn"
              className="bg-white text-[#00A2E8] hover:bg-slate-100 px-8 py-4 font-black text-sm tracking-widest transition-colors duration-200"
            >
              GET A FREE QUOTE
            </Link>
            <a
              href="tel:+14165703834"
              data-testid="cta-phone-btn"
              className="border-2 border-white text-white hover:bg-white/10 px-8 py-4 font-bold text-sm tracking-widest transition-all duration-200 flex items-center justify-center gap-2"
            >
              <Phone className="w-4 h-4" /> +1 416 570 3834
            </a>
          </div>
        </div>
      </section>

    </main>
  );
}
